/**
 * Inline decline-confirmation expander for the Hub action strip.
 *
 * Spec §9.1 lists "Decline" as an expert action in `PENDING_OFFER`. Spec
 * §11.2 #12 flagged the decline-flow UX as unspecified; this component is
 * the prototype's answer.
 *
 * Pattern: mirrors `GovernanceApproverInbox/InboxRow.tsx` `RejectExpander`,
 * with two intentional softenings:
 *  - Note is **not hard-required** (decline is "no thanks," not a blocking
 *    judgment requiring justification — contrast with governance rejection
 *    per §9.7 which requires a note).
 *  - Confirm button is **secondary** rather than destructive — declining a
 *    pre-offer engagement is a normal-course outcome, not a destructive act.
 *
 * **But the note is strongly encouraged, not merely permitted.** Confirming
 * a decline with an empty reason triggers an inline soft-confirmation prompt
 * with "Add a reason" / "Decline anyway" — friction-when-skipping rather
 * than friction-when-noting. This matches the "highly encourage but don't
 * require" intent from product review of the initial Session 8 build.
 *
 * Render context: Hub Overview action strip (`OverviewTab.tsx PrimaryActions`)
 * when viewer is the expert and `status === 'pending_offer'` and the expert
 * has clicked Decline. The Make Offer drawer's footer Decline button stays
 * render-only in this prototype — the Hub strip is the canonical entry per
 * the Session 8 scope decision.
 */
import { useRef, useState } from 'react';

import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

interface DeclineConfirmationProps {
  /** Customer creator's display name; used in helper-text copy. */
  creatorName: string;
  /**
   * Story-only seed for the reason textarea. Defaults to empty (the
   * blank-state opening). The "with note" story variant pre-fills this so
   * the helper-text + filled-textarea state is renderable without typing.
   */
  initialNote?: string;
  /** Disables the commit affordances while a decline is in flight. */
  busy?: boolean;
  /** Commit the decline. Note may be empty (after soft-confirm passthrough). */
  onConfirm: (note: string) => void;
  /** Collapse the expander without action. */
  onCancel: () => void;
}

export default function DeclineConfirmation({
  creatorName,
  initialNote = '',
  busy = false,
  onConfirm,
  onCancel,
}: DeclineConfirmationProps): JSX.Element {
  const [note, setNote] = useState(initialNote);
  /**
   * True when the expert clicked Confirm decline with an empty reason and
   * we're showing the soft-confirmation prompt. Cleared when they either
   * (a) click "Add a reason" — returns to the editing state, or
   * (b) click "Decline anyway" — passes through to onConfirm with empty note.
   */
  const [softConfirmBlank, setSoftConfirmBlank] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const trimmed = note.trim();
  const hasNote = trimmed.length > 0;

  function handleConfirm() {
    if (!hasNote) {
      setSoftConfirmBlank(true);
      return;
    }
    onConfirm(trimmed);
  }

  function handleAddReason() {
    setSoftConfirmBlank(false);
    // Best-effort textarea focus. Input wraps a textarea in `multiline` mode;
    // we don't depend on ref-forwarding — just query the rendered DOM. The
    // setTimeout ensures the textarea is mounted (it always is, but defensive)
    // before we focus.
    setTimeout(() => {
      wrapperRef.current?.querySelector('textarea')?.focus();
    }, 0);
  }

  function handleDeclineAnyway() {
    setSoftConfirmBlank(false);
    onConfirm('');
  }

  return (
    <div
      ref={wrapperRef}
      className="flex flex-col gap-12 rounded-md border border-surface-border bg-surface-elevated px-16 py-16"
    >
      <div className="flex flex-col gap-2">
        <span className="text-on-surface subtitle-2">Decline this engagement</span>
        <span className="text-on-surface-muted body-2">The engagement will be canceled.</span>
      </div>
      <Input
        label="Reason for declining"
        multiline
        rows={3}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          // If they start typing, dismiss the blank-confirm prompt — they're
          // adding the reason we asked for.
          if (softConfirmBlank) setSoftConfirmBlank(false);
        }}
        helperText={`A brief reason helps ${creatorName} understand and gives them useful context for finding another expert. It also preserves your relationship with the customer for future engagements.`}
        placeholder="e.g., Booked through end of Q2, conflict of interest, topic outside my expertise…"
      />
      {softConfirmBlank ? (
        <div
          className="flex flex-col gap-8 rounded-md border border-yellow-700/40 bg-yellow-50 px-12 py-8 dark:bg-yellow-900/20"
          role="alertdialog"
          aria-label="Confirm decline without a reason"
        >
          <span className="text-yellow-900 body-2 dark:text-yellow-200">
            Decline without a reason? A note is strongly recommended — it takes 10 seconds and keeps
            the customer relationship intact.
          </span>
          <div className="flex justify-end gap-8">
            <Button variant="tertiary" size="small" onClick={handleDeclineAnyway} disabled={busy}>
              Decline anyway
            </Button>
            <Button variant="secondary" size="small" onClick={handleAddReason}>
              Add a reason
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-8">
          <Button variant="tertiary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="secondary" size="small" onClick={handleConfirm} disabled={busy}>
            Confirm decline
          </Button>
        </div>
      )}
    </div>
  );
}
